import { engineComponent, StringUtils, Utils } from "neo-common";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { HtmlContainerElement } from "../Elements/HtmlContainerElement";
import { HtmlButtonElement } from "../Elements/HtmlButtonElement";
import { BaseElement } from "../../Components/Elements/Graphics/BaseElement";
import { Scaling } from "../../Utils/Scaling";
import { StageController } from "../../Components/StageController";
import EventEmitter = PIXI.utils.EventEmitter;
import { HtmlController } from "../HtmlController";
import { GridMagnifier } from "../../GameComponents/GridMagnifier";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";

@engineComponent(ComponentTypes.HtmlElement)
export class HtmlRouletteTableElement extends HtmlContainerElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private static readonly MagnifierStartDelay: number = 20;

    public static MouseOverBet: string = "eventMouseOverBet";
    public static MouseOutBet: string = "eventMouseOutBet";

    public static Emitter: EventEmitter;

    private lastElement: Element;

    private disabledButton: boolean;

    private magnifierTimer: FrameTimerEventEmitter;
    private lastX: number;
    private lastY: number;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor(
        parentId: string,
        id: string,
        className: string,
        canvasElement: BaseElement,
        width: number,
        height: number,
    ) {
        super();

        if (HtmlRouletteTableElement.Emitter == null) {
            HtmlRouletteTableElement.Emitter = new EventEmitter();
        }

        this.magnifierTimer = CreateFrameTimer("magnifierTimer");
        this.magnifierTimer.on(FrameTimerEvents.Complete, this.showMagnifier.bind(this));

        this.instance.className = className;
        this.instance.id = id;
        this.setupInstanceHtmlElement(canvasElement, width, height);

        let root = document.getElementById(parentId);
        if (root) {
            root.appendChild(this.instance);
        }
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    public setupInstanceHtmlElement(elem: BaseElement, width: number, height: number): void {
        this.instance.style.width = width + "px";
        this.instance.style.height = height + "px";
        this.instance.style.top = elem.y - elem.pivot.y + "px";
        this.instance.style.left = elem.x - elem.pivot.x + "px";
        this.instance.style.transformOrigin = elem.pivot.x + "px " + elem.pivot.y + "px";
        this.instance.style.transform = "scale(" + elem.scale.x + ") rotate(" + elem.rotation + "rad)";
    }

    protected setupHtmlElement(elem: HTMLElement, x: number, y: number, width: number, height: number): void {
        elem.style.width = width > -1 ? width + "px" : "";
        elem.style.height = height > -1 ? height + "px" : "";
        elem.style.top = y + "px";
        elem.style.left = x + "px";
    }

    protected createHtmlButton(action: string): HtmlButtonElement {
        let node: Element = document.createElement("div");
        node.setAttribute("CssClass", "gridBtn");
        node.setAttribute("Action", action);

        let elem = new HtmlButtonElement();
        elem.create(node);

        return elem;
    }

    //******************************************************************************************************************
    //  MOUSE EVENTS
    //******************************************************************************************************************

    protected onHtmlBetPlaceMouseOver(betType: string, selection: string, fromRacetrack: boolean): void {
        if (HtmlRouletteTableElement.Emitter) {
            if (window["CHANNEL"] !== "mobile") {
                HtmlRouletteTableElement.Emitter.emit(
                    HtmlRouletteTableElement.MouseOverBet,
                    betType,
                    selection,
                    fromRacetrack,
                );
            }
        }
    }

    protected onHtmlBetPlaceMouseOut(betType: string, selection: string, fromRacetrack: boolean): void {
        if (HtmlRouletteTableElement.Emitter) {
            if (window["CHANNEL"] !== "mobile") {
                HtmlRouletteTableElement.Emitter.emit(
                    HtmlRouletteTableElement.MouseOutBet,
                    betType,
                    selection,
                    fromRacetrack,
                );
            }
        }
    }

    //******************************************************************************************************************
    //  TOUCH EVENTS
    //******************************************************************************************************************

    protected onHtmlBetPlaceTouchStart(
        event: TouchEvent,
        betType: string,
        selection: string,
        fromRacetrack: boolean,
    ): void {
        this.disabledButton = this.isButtonDisabled(betType + "_" + selection);

        if (this.disabledButton) {
            return;
        }

        if (event && event.targetTouches && event.targetTouches.length > 0) {
            this.lastElement = document.elementFromPoint(event.targetTouches[0].pageX, event.targetTouches[0].pageY);
            this.lastX = event.targetTouches[0].pageX;
            this.lastY = event.targetTouches[0].pageY;

            this.magnifierTimer.stop();
            this.magnifierTimer.start(HtmlRouletteTableElement.MagnifierStartDelay);

            if (HtmlRouletteTableElement.Emitter) {
                HtmlRouletteTableElement.Emitter.emit(
                    HtmlRouletteTableElement.MouseOverBet,
                    betType,
                    selection,
                    fromRacetrack,
                );
            }
        }
    }

    protected onHtmlBetPlaceTouchMove(event: TouchEvent, fromRacetrack: boolean): void {
        if (this.disabledButton) {
            return;
        }

        if (event && event.targetTouches && event.targetTouches.length > 0) {
            let elem = document.elementFromPoint(event.targetTouches[0].pageX, event.targetTouches[0].pageY);
            let bet: { betType: string; selection: string };
            this.lastX = event.targetTouches[0].pageX;
            this.lastY = event.targetTouches[0].pageY;

            this.showMagnifier();

            if (elem != null && elem != this.lastElement) {
                //reset last element
                if (this.lastElement != null) {
                    bet = this.getBetTypeFromElement(this.lastElement);

                    if (bet != null) {
                        HtmlRouletteTableElement.Emitter.emit(
                            HtmlRouletteTableElement.MouseOutBet,
                            bet.betType,
                            bet.selection,
                            fromRacetrack,
                        );
                    }
                }

                //emit new element
                bet = this.getBetTypeFromElement(elem);

                if (bet != null) {
                    HtmlRouletteTableElement.Emitter.emit(
                        HtmlRouletteTableElement.MouseOverBet,
                        bet.betType,
                        bet.selection,
                        fromRacetrack,
                    );
                    this.lastElement = elem;
                } else {
                    this.lastElement = null;
                }

                // console.log(elem.getAttribute('id') + " x: " + event.targetTouches[0].pageX + " y: " + event.targetTouches[0].pageY);
            }
        }
    }

    protected onHtmlBetPlaceTouchEnd(event: TouchEvent, fromRacetrack: boolean): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.disabledButton) {
            return;
        }

        this.hideMagnifier();

        if (this.lastElement != null) {
            let bet = this.getBetTypeFromElement(this.lastElement);

            if (bet != null) {
                HtmlRouletteTableElement.Emitter.emit(
                    HtmlRouletteTableElement.MouseOutBet,
                    bet.betType,
                    bet.selection,
                    fromRacetrack,
                );

                if (!this.lastElement.classList.contains("_disabled")) {
                    HtmlController.dispatchAction(this.lastElement.getAttribute("id"));
                }
            }
        }
    }

    //******************************************************************************************************************
    //  MAGNIFIER
    //******************************************************************************************************************

    protected showMagnifier(): void {
        this.magnifierTimer.stop();

        if (this.lastElement != null) {
            if (
                this.lastElement.hasAttribute("data-magnifier") &&
                StringUtils.parseBoolean(this.lastElement.getAttribute("data-magnifier"))
            ) {
                GridMagnifier.touchMoveMagnifier(this.lastX, this.lastY);
            } else {
                GridMagnifier.touchEndMagnifier();
            }
        } else {
            GridMagnifier.touchEndMagnifier();
        }
    }

    protected hideMagnifier(): void {
        this.magnifierTimer.stop();
        GridMagnifier.touchEndMagnifier();
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    protected getBetTypeFromElement(elem: Element): { betType: string; selection: string } {
        let result: { betType: string; selection: string } = null;

        let betType = elem.hasAttribute("id") ? elem.getAttribute("id").split("_") : null;

        if (betType && betType.length == 2) {
            result = {
                betType: betType[0],
                selection: betType[1],
            };
        }

        return result;
    }

    protected isButtonDisabled(actionId: string): boolean {
        if (HtmlController.ActionElements[actionId] != null && HtmlController.ActionElements[actionId].length > 0) {
            return HtmlController.ActionElements[actionId][0].instance.classList.contains(HtmlController.DisabledClass);
        }

        return false;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/HTMLComponents/Custom/HtmlRouletteTableElement.ts
 **/